import { useEffect, useState } from "react";
import PatientPageHeading from "../../components/organisms/patient-dashboard/PatientPageHeading";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  ajouterContactSurete,
  listerContactsSurete,
  modifierContactSurete,
  renvoyerConfirmationContactSurete,
  supprimerContactSurete,
  type ContactSurete,
} from "../../api/contactSurete";

function statutLabel(statut: ContactSurete["statutConfirmation"]): { label: string; variant: "soft" | "outline" } {
  switch (statut) {
    case "confirme":
      return { label: "Confirmé", variant: "soft" };
    case "en_attente":
      return { label: "En attente de confirmation", variant: "outline" };
    case "refuse":
      return { label: "Refusé", variant: "outline" };
    default:
      return { label: "Non soumis", variant: "outline" };
  }
}

export default function PatientContactsSurete() {
  const [contacts, setContacts] = useState<ContactSurete[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [nom, setNom] = useState("");
  const [email, setEmail] = useState("");
  const [telephone, setTelephone] = useState("");
  const [relation, setRelation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const res = await listerContactsSurete();
    if (res.ok) {
      setContacts(res.data);
      setError(null);
    } else {
      setError(res.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    void load();
  }, []);

  const nextPriorite = (contacts.reduce((max, c) => Math.max(max, c.priorite), 0) || 0) + 1;

  const handleAdd = async () => {
    if (!nom.trim() || !email.trim() || !relation.trim()) return;
    setSubmitting(true);
    setFormError(null);
    const res = await ajouterContactSurete({
      nom: nom.trim(),
      email: email.trim(),
      telephone: telephone.trim() || undefined,
      relation: relation.trim(),
      priorite: nextPriorite,
    });
    setSubmitting(false);
    if (!res.ok) {
      setFormError(res.message);
      return;
    }
    setNom("");
    setEmail("");
    setTelephone("");
    setRelation("");
    await load();
  };

  const handleRemove = async (id: string) => {
    await supprimerContactSurete(id);
    await load();
  };

  const handleResend = async (id: string) => {
    await renvoyerConfirmationContactSurete(id);
    await load();
  };

  const handleMove = async (contact: ContactSurete, direction: -1 | 1) => {
    const sorted = [...contacts].sort((a, b) => a.priorite - b.priorite);
    const index = sorted.findIndex((c) => c.id === contact.id);
    const swapWith = sorted[index + direction];
    if (!swapWith) return;
    await modifierContactSurete(contact.id, { priorite: swapWith.priorite });
    await modifierContactSurete(swapWith.id, { priorite: contact.priorite });
    await load();
  };

  const sortedContacts = [...contacts].sort((a, b) => a.priorite - b.priorite);

  return (
    <div className="space-y-6">
      <PatientPageHeading
        title="Contacts de sûreté"
        description="Désignez une ou plusieurs personnes de confiance capables d'autoriser un accès à votre dossier si vous êtes injoignable. Chaque contact doit confirmer son rôle par email avant d'être actif."
      />

      <Card>
        <CardHeader>
          <CardTitle>Vos contacts</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {loading && <p className="text-sm text-gray-500">Chargement…</p>}
          {error && !loading && <p className="text-sm text-red-600">{error}</p>}
          {!loading && sortedContacts.length === 0 && (
            <p className="text-sm text-gray-500">Aucun contact de sûreté pour le moment.</p>
          )}
          {!loading &&
            sortedContacts.map((contact, index) => {
              const statut = statutLabel(contact.statutConfirmation);
              return (
                <div
                  key={contact.id}
                  className="flex items-center justify-between gap-4 rounded-[10px] border bg-[#FAFBFB] p-3"
                  style={{ borderColor: "#E5E7EB" }}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-gray-500">Priorité {contact.priorite}</span>
                      <Badge variant={statut.variant}>{statut.label}</Badge>
                    </div>
                    <div className="mt-1 font-semibold text-[#0B1F16] truncate">
                      {contact.nom} — {contact.relation}
                    </div>
                    <div className="text-sm text-gray-600 truncate">
                      {contact.email}
                      {contact.telephone ? ` · ${contact.telephone}` : ""}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Button size="sm" variant="ghost" disabled={index === 0} onClick={() => void handleMove(contact, -1)}>
                      ↑
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={index === sortedContacts.length - 1}
                      onClick={() => void handleMove(contact, 1)}
                    >
                      ↓
                    </Button>
                    {contact.statutConfirmation !== "confirme" && (
                      <Button size="sm" variant="outline" onClick={() => void handleResend(contact.id)}>
                        Renvoyer
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => void handleRemove(contact.id)}>
                      Retirer
                    </Button>
                  </div>
                </div>
              );
            })}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Ajouter un contact</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="contact-nom" className="block text-sm font-medium text-[#0B1F16] mb-1">
                Nom
              </label>
              <input
                id="contact-nom"
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                className="w-full rounded-[8px] border border-black/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0CA37F]/40"
              />
            </div>
            <div>
              <label htmlFor="contact-email" className="block text-sm font-medium text-[#0B1F16] mb-1">
                Email
              </label>
              <input
                id="contact-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-[8px] border border-black/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0CA37F]/40"
              />
            </div>
            <div>
              <label htmlFor="contact-telephone" className="block text-sm font-medium text-[#0B1F16] mb-1">
                Téléphone (optionnel)
              </label>
              <input
                id="contact-telephone"
                value={telephone}
                onChange={(e) => setTelephone(e.target.value)}
                className="w-full rounded-[8px] border border-black/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0CA37F]/40"
              />
            </div>
            <div>
              <label htmlFor="contact-relation" className="block text-sm font-medium text-[#0B1F16] mb-1">
                Relation
              </label>
              <input
                id="contact-relation"
                value={relation}
                onChange={(e) => setRelation(e.target.value)}
                placeholder="Conjoint, parent, ami…"
                className="w-full rounded-[8px] border border-black/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0CA37F]/40"
              />
            </div>
          </div>
          {formError && <p className="text-sm text-red-600">{formError}</p>}
          <Button
            type="button"
            disabled={submitting || !nom.trim() || !email.trim() || !relation.trim()}
            onClick={() => void handleAdd()}
          >
            {submitting ? "Ajout…" : "Ajouter le contact"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
